import { useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { Loader2, X } from "lucide-react";

const REASONS = [
  { key: "backload", label: "Backload" },
  { key: "repeat_customer", label: "Repeat customer" },
  { key: "agreed_price", label: "Agreed price" },
  { key: "promotional_price", label: "Promotional price" },
  { key: "manual_adjustment", label: "Manual adjustment" },
  { key: "other", label: "Other" },
];

const money = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`);

export default function PriceEditModal({ booking, onClose, onSaved }) {
  const [newPrice, setNewPrice] = useState("");
  const [reason, setReason] = useState("backload");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const original = booking.original_instant_booking_price ?? booking.instant_booking_price;
  const current = booking.manual_price_override ?? booking.instant_booking_price;
  const hasOverride = booking.manual_price_override != null;
  const paid = Number(booking.deposit_paid || 0);

  async function save() {
    const n = parseFloat(newPrice);
    if (!n || n <= 0) {
      toast.error("Enter a valid new price.");
      return;
    }
    if (reason === "other" && !note.trim()) {
      toast.error("A short note is required when the reason is Other.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/admin/bookings/${booking.booking_ref}/price`, {
        new_price: n,
        reason,
        note: note.trim(),
      });
      toast.success(`Price updated to ${money(data.new_price)}`);
      if (data.warning) toast.warning(data.warning, { duration: 6000 });
      onSaved?.(data.booking);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed.");
    } finally {
      setSaving(false);
    }
  }

  async function restore() {
    if (!window.confirm("Restore automatic pricing for this booking?")) return;
    setRestoring(true);
    try {
      const { data } = await apiClient.post(`/admin/bookings/${booking.booking_ref}/restore-price`);
      toast.success("Automatic price restored.");
      onSaved?.(data.booking);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Restore failed.");
    } finally {
      setRestoring(false);
    }
  }

  return (
    <Backdrop onClose={onClose}>
      <div
        data-testid="admin-price-edit-modal"
        className="w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
              Edit Price · {booking.booking_ref}
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mt-2">
              Manual price override
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="admin-price-edit-close">
            <X className="w-5 h-5 text-zinc-400 hover:text-white" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <Stat label="Original Calculated Price" value={money(original)} />
          <Stat label="Current Customer Price" value={money(current)} highlight={hasOverride} />
        </div>

        {paid > 0 && (
          <div className="mb-4 text-[10px] uppercase tracking-[0.2em] text-yellow-400 border border-yellow-500/30 bg-yellow-500/5 p-3">
            Deposit already paid: {money(paid)}. Changing the price will NOT auto-refund or auto-charge.
          </div>
        )}

        <div className="space-y-4">
          <Field label="New Agreed Transport Price (£)">
            <input
              data-testid="admin-price-edit-input"
              type="number"
              min={1}
              step="0.01"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              placeholder="e.g. 350"
              className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000]"
            />
          </Field>

          <Field label="Reason for adjustment">
            <select
              data-testid="admin-price-edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base focus:outline-none focus:border-[#E60000] appearance-none cursor-pointer"
            >
              {REASONS.map((r) => (
                <option key={r.key} className="bg-[#0A0A0A]" value={r.key}>{r.label}</option>
              ))}
            </select>
          </Field>

          {reason === "other" && (
            <Field label="Note">
              <input
                data-testid="admin-price-edit-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Brief note (required)"
                maxLength={400}
                className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-base placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000]"
              />
            </Field>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
          <div>
            {hasOverride && !booking.is_manual_quote && (
              <button
                data-testid="admin-price-restore-btn"
                onClick={restore}
                disabled={restoring}
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 hover:border-[#E60000] hover:text-white transition-colors disabled:opacity-50"
              >
                {restoring ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Remove manual override
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-price-edit-cancel"
              onClick={onClose}
              className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="admin-price-edit-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save new price
            </button>
          </div>
        </div>

        {Array.isArray(booking.price_history) && booking.price_history.length > 0 && (
          <div className="mt-8 border-t border-white/10 pt-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
              Price history
            </div>
            <ul className="space-y-2 max-h-40 overflow-y-auto pr-2 text-xs">
              {booking.price_history.slice().reverse().map((h, i) => (
                <li key={i} className="flex flex-wrap justify-between gap-2 text-zinc-400 border-b border-white/5 pb-2">
                  <span>
                    <span className="text-white font-semibold">{money(h.price)}</span>
                    {h.previous_price != null && (
                      <span className="text-zinc-500"> · from {money(h.previous_price)}</span>
                    )}
                    <span className="text-zinc-500"> · {h.reason?.replace(/_/g, " ")}</span>
                    {h.note && <span className="italic text-zinc-500"> — {h.note}</span>}
                  </span>
                  <span className="text-zinc-600">
                    {h.at ? new Date(h.at).toLocaleString("en-GB") : ""} · {h.admin}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto py-8 px-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#E60000]/40" : "border-white/10"} bg-[#050505] p-4`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 ${highlight ? "text-[#E60000]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">{label}</div>
      {children}
    </label>
  );
}
